import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtDate, fmtHours, formatApiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHero, useTableSort, SortableTH, RagBadge, Avatar, DateField } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { UserPlus, Upload, Download, FileUp, Users } from "lucide-react";
import { toast } from "sonner";

const REGIONS = [
  { v: "england-and-wales", l: "England & Wales" },
  { v: "scotland", l: "Scotland" },
  { v: "northern-ireland", l: "Northern Ireland" },
];

export default function Employees() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [managers, setManagers] = useState([]);
  const [balances, setBalances] = useState({});
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const load = () => {
    api.get("/employees").then((r) => { setRows(r.data); setManagers(r.data); }).catch(() => {});
    api.get("/employees/balances").then((r) => {
      const m = {}; r.data.forEach((b) => { m[b.employee_id] = b; }); setBalances(m);
    }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const filtered = rows.filter((e) => `${e.first_name} ${e.last_name} ${e.employee_code} ${e.department || ""}`.toLowerCase().includes(q.toLowerCase()));
  const mgrName = (e) => { const m = rows.find((x) => x.id === e.manager_id); return m ? `${m.first_name} ${m.last_name}` : ""; };
  const { sorted, sort, toggle } = useTableSort(filtered, {
    code: (e) => e.employee_code, name: (e) => `${e.first_name} ${e.last_name}`,
    department: (e) => e.department, manager: mgrName, start: (e) => e.start_date,
    region: (e) => e.region, status: (e) => (e.active ? "Active" : "Inactive"),
    remaining: (e) => balances[e.id]?.confirmed_remaining ?? -1,
  }, { key: "name", dir: "asc" });

  return (
    <div className="space-y-5">
      <PageHero title="People" subtitle="Browse colleagues, holiday balances and records across CASS." icon={Users} pageKey="people" stats={[{ label: "People", value: rows.length }]}>
        {user?.role === "admin" && (
          <>
            <ImportDialog onDone={load} />
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-employee-btn" className="bg-white text-[#0B1A2F] hover:bg-slate-100"><UserPlus size={16} className="mr-2" />Add employee</Button>
              </DialogTrigger>
              <EmployeeDialog managers={managers} onSaved={() => { setOpen(false); load(); }} />
            </Dialog>
          </>
        )}
      </PageHero>

      <Input placeholder="Search name, code or department…" value={q} onChange={(e) => setQ(e.target.value)}
        data-testid="employee-search" aria-label="Search employees" className="max-w-sm bg-white" />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {sorted.map((e) => {
          const b = balances[e.id];
          const total = b?.total_entitlement || 0;
          const rem = b?.confirmed_remaining || 0;
          const booked = Math.max(0, +(total - rem).toFixed(2));
          const pct = total > 0 ? Math.min(100, Math.round((booked / total) * 100)) : 0;
          const initials = `${e.first_name?.[0] || ""}${e.last_name?.[0] || ""}`.toUpperCase();
          const highUnused = b?.rag === "amber" && total > 0 && rem / total > 0.5;
          return (
            <div key={e.id} data-testid={`employee-card-${e.employee_code}`}
              className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all flex flex-col">
              <div className="flex items-center gap-3">
                <span className="h-11 w-11 rounded-full bg-[#E0F2FE] text-[#0B1A2F] grid place-items-center font-bold overflow-hidden ring-1 ring-white shrink-0">
                  {e.photo_url ? <img src={e.photo_url} alt="" className="h-full w-full object-cover" /> : initials || "?"}
                </span>
                <div className="min-w-0">
                  <p className="font-semibold text-[#0B1A2F] truncate">{e.first_name} {e.last_name}</p>
                  <p className="text-xs text-slate-500 truncate">{e.department || "—"}</p>
                </div>
                {!e.active && <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Inactive</span>}
              </div>

              {b ? (
                <>
                  <div className="mt-4 space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-slate-500">Allowance</span><span className="tabular font-semibold text-[#0B1A2F]">{fmtHours(total)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Booked / taken</span><span className="tabular font-semibold text-[#0B1A2F]">{fmtHours(booked)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Available</span><span className="tabular font-semibold text-[#0B1A2F]">{fmtHours(rem)}</span></div>
                  </div>
                  <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full bg-[#2EA3FD] rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  {highUnused && <p className="text-xs text-amber-600 mt-2 font-medium">High unused balance</p>}
                </>
              ) : (
                <p className="text-xs text-slate-400 mt-4">No balance calculated yet</p>
              )}

              <Button variant="outline" data-testid={`view-employee-${e.employee_code}`}
                onClick={() => nav(`/employees/${e.id}`)} className="mt-4 w-full font-semibold">View employee</Button>
            </div>
          );
        })}
        {filtered.length === 0 && <div className="col-span-full text-center text-slate-400 py-12 bg-white rounded-xl border border-slate-200">No employees</div>}
      </div>
    </div>
  );
}

function EmployeeDialog({ managers, onSaved }) {
  const [f, setF] = useState({
    employee_code: "", first_name: "", last_name: "", email: "", department: "", manager_id: "none",
    start_date: "", end_date: "", region: "england-and-wales", bank_holiday_treatment: "additional",
    active: true, create_account: true, account_role: "employee", account_password: "", date_of_birth: "",
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    try {
      const payload = { ...f, manager_id: f.manager_id === "none" ? null : f.manager_id, end_date: f.end_date || null };
      await api.post("/employees", payload);
      toast.success("Employee created");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader><DialogTitle>Add employee</DialogTitle></DialogHeader>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Employee code"><Input data-testid="emp-code-input" value={f.employee_code} onChange={(e) => set("employee_code", e.target.value)} /></Field>
        <Field label="Department"><Input value={f.department} onChange={(e) => set("department", e.target.value)} /></Field>
        <Field label="First name"><Input data-testid="emp-first-input" value={f.first_name} onChange={(e) => set("first_name", e.target.value)} /></Field>
        <Field label="Last name"><Input data-testid="emp-last-input" value={f.last_name} onChange={(e) => set("last_name", e.target.value)} /></Field>
        <Field label="Work email"><Input data-testid="emp-email-input" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} /></Field>
        <Field label="Manager">
          <Select value={f.manager_id} onValueChange={(v) => set("manager_id", v)}>
            <SelectTrigger data-testid="emp-manager-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No manager</SelectItem>
              {managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.first_name} {m.last_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Start date"><Input data-testid="emp-start-input" type="date" value={f.start_date} onChange={(e) => set("start_date", e.target.value)} /></Field>
        <Field label="End date (optional)"><Input type="date" value={f.end_date} onChange={(e) => set("end_date", e.target.value)} /></Field>
        <Field label="Date of birth (for birthday leave)"><DateField value={f.date_of_birth} onChange={(v) => set("date_of_birth", v)} testid="emp-dob-input" /></Field>
        <Field label="Holiday region">
          <Select value={f.region} onValueChange={(v) => set("region", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{REGIONS.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Bank-holiday treatment">
          <Select value={f.bank_holiday_treatment} onValueChange={(v) => set("bank_holiday_treatment", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="additional">Additional to annual leave</SelectItem>
              <SelectItem value="included">Included in entitlement</SelectItem>
              <SelectItem value="excluded">Excluded from tracker</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>
      <div className="border-t pt-4 space-y-3">
        <label className="flex items-center gap-2 text-sm">
          <Checkbox data-testid="emp-create-account" checked={f.create_account} onCheckedChange={(v) => set("create_account", !!v)} />
          Create a login account for this employee
        </label>
        {f.create_account && (
          <div className="grid grid-cols-2 gap-4">
            <Field label="Account role">
              <Select value={f.account_role} onValueChange={(v) => set("account_role", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="employee">Employee</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="admin">Administrator</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Temporary password"><Input value={f.account_password} placeholder="Welcome2026!" onChange={(e) => set("account_password", e.target.value)} /></Field>
          </div>
        )}
      </div>
      <DialogFooter>
        <Button data-testid="emp-save-btn" onClick={save} className="bg-[#2EA3FD] hover:bg-[#1B8EE6]">Save employee</Button>
      </DialogFooter>
    </DialogContent>
  );
}

const Field = ({ label, children }) => (
  <div><Label className="text-xs text-slate-500 mb-1 block">{label}</Label>{children}</div>
);

function ImportDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const upload = async () => {
    if (!file) { toast.error("Choose a CSV or Excel file first"); return; }
    setLoading(true); setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/employees/import", fd);
      setResult(data);
      toast.success(`Imported ${data.created} of ${data.total} row(s)`);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setResult(null); setFile(null); } }}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="import-employees-btn"><Upload size={16} className="mr-2" />Import</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Import employees</DialogTitle>
          <DialogDescription>Bulk-create employees from a CSV or Excel file.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-slate-600">Upload a CSV or Excel file. Required columns: <span className="font-mono text-xs">employee_code, first_name, last_name, email, start_date</span>. Optional: department, manager_code, end_date, region, bank_holiday_treatment, allowance_weeks, mon–sun (daily hours), create_account, account_role.</p>
          <Button variant="outline" size="sm" onClick={() => window.open(`${API}/employees/import-template`, "_blank")} data-testid="download-template-btn">
            <Download size={15} className="mr-2" />Download CSV template
          </Button>
          <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
            <FileUp size={28} className="mx-auto text-slate-400 mb-2" />
            <Input type="file" accept=".csv,.xlsx" onChange={(e) => setFile(e.target.files[0])} data-testid="import-file-input" className="cursor-pointer" />
            {file && <p className="text-xs text-slate-500 mt-2">{file.name}</p>}
          </div>
          <p className="text-xs text-slate-400">New login accounts (create_account = yes) get the temporary password <span className="font-mono">Welcome2026!</span>.</p>

          {result && (
            <div className="rounded-lg border border-slate-200 p-3 max-h-52 overflow-y-auto cass-scroll" data-testid="import-result">
              <p className="text-sm font-semibold text-emerald-700">✓ {result.created} employee(s) imported</p>
              {result.errors?.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm font-semibold text-amber-700">{result.errors.length} row(s) with issues:</p>
                  <ul className="text-xs text-slate-600 mt-1 space-y-0.5">
                    {result.errors.map((er, i) => <li key={i}>Row {er.row}{er.code ? ` (${er.code})` : ""}: {er.message}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={upload} disabled={loading} className="bg-[#2EA3FD] hover:bg-[#1B8EE6]" data-testid="import-upload-btn">
            {loading ? "Importing…" : "Upload & import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
